import { useState } from 'react'
import { Box, Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react'


// `title` and `subjectLabel` are optional so other callers (e.g. the Delivery Logs message
// column, where the content is a sent message rather than a template) can label the modal
// and its metadata line for their own context.
const EmailPreviewModal = ({
    onEmailPreviewClose,
    isEmailPreviewOpen,
    subject,
    body,
    title = 'Email Preview',
    subjectLabel = 'Subject:',
    // Optional escape hatch for callers whose stored body cannot render (see the
    // digit-masking note below): render something accurate from another source instead.
    onFallbackPreview,
    fallbackLabel = 'Preview the source template instead',
    fallbackLoading = false,
    fallbackNote,
}) => {
    const [showSource, setShowSource] = useState(false)

    const html = body || ''
    // A rendered email that arrives with no styling and broken images is almost always a
    // STORED-CONTENT problem (styles/src stripped, quoted-printable `=3D` left in place,
    // entity-escaped markup), not a rendering one — and the two are indistinguishable in
    // an iframe. The source view makes the difference visible in one click.
    const looksEncoded = /=3D|=\r?\n/.test(html)
    const looksEscaped = /&lt;(table|div|p|img|a|span)\b/i.test(html)
    const hasInlineStyles = /\sstyle\s*=\s*["']/i.test(html)
    const hasImages = /<img\b/i.test(html)
    const hasImageSrc = /<img[^>]+src\s*=\s*["'][^"']+["']/i.test(html)

    // Digit masking. The delivery-log pipeline replaces every digit in the message body
    // with `****`, which is unrecoverable for HTML: `<h1>` becomes `<h****>` (not a
    // heading), `padding:16px` becomes `padding:****px` (dropped as invalid CSS),
    // `border="0"` becomes `border="****"` (a truthy border attribute, so the browser
    // draws 1px rules around every table) and any image URL containing a digit 404s.
    // Detected on the tell-tale signatures rather than a bare `****`, which could legitimately
    // appear in copy.
    const looksDigitMasked = /<h\*{2,}|border="\*{2,}"|:\s*\*{2,}px|#\*{2,}|width="\*{2,}%/.test(html)

    // Only digit masking suppresses the render. The other findings still produce something
    // recognisable, so those previews stay visible with the warning above them.
    const hideBrokenRender = looksDigitMasked && !showSource

    const diagnostics = []
    if (looksDigitMasked) diagnostics.push('Numbers in this message were masked to **** by the logging pipeline, so tag names, CSS values and image URLs are all corrupted. It cannot render accurately — this needs fixing where the log is written, not here.')
    if (looksEncoded) diagnostics.push('Contains quoted-printable escapes (=3D / soft line breaks) — attributes will not parse.')
    if (looksEscaped) diagnostics.push('Markup is HTML-entity escaped (&lt;table&gt;) rather than raw.')
    if (html && !hasInlineStyles) diagnostics.push('No inline style attributes found — styling was likely stripped before storage.')
    if (hasImages && !hasImageSrc) diagnostics.push('Image tags have no usable src — image URLs were likely stripped before storage.')

    return (
        <Modal isOpen={isEmailPreviewOpen} onClose={onEmailPreviewClose} size={"5xl"} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{title}</ModalHeader>
                <ModalCloseButton />
                <ModalBody px={6}>
                    {subject && (
                        <Box mb={3}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.600">{subjectLabel}</Text>
                            <Text fontSize="md" color="gray.800">{subject}</Text>
                        </Box>
                    )}

                    <Box>
                        <Flex align="center" justify="space-between" mb={2} gap={3}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.600">Content:</Text>
                            {html && (
                                <Button size="xs" variant="link" color="var(--brand-primary)" onClick={() => setShowSource((v) => !v)}>
                                    {showSource ? 'View rendered' : 'View HTML source'}
                                </Button>
                            )}
                        </Flex>

                        {fallbackNote && (
                            <Box mb={2} p={2} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md">
                                <Text fontSize="xs" color="blue.800">• {fallbackNote}</Text>
                            </Box>
                        )}

                        {diagnostics.length > 0 && (
                            // No trailing gap when nothing is rendered beneath it.
                            <Box mb={hideBrokenRender ? 0 : 2} p={2} bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md">
                                {diagnostics.map((d) => (
                                    <Text key={d} fontSize="xs" color="orange.800">• {d}</Text>
                                ))}
                                {onFallbackPreview && (
                                    <Button
                                        size="xs"
                                        mt={2}
                                        colorScheme="orange"
                                        variant="outline"
                                        isLoading={fallbackLoading}
                                        onClick={onFallbackPreview}
                                    >
                                        {fallbackLabel}
                                    </Button>
                                )}
                            </Box>
                        )}

                        {/* Digit-masked content is suppressed rather than rendered: a broken
                            preview invites people to debug the layout when the markup itself is
                            corrupt. The source view stays reachable from the toggle above, and
                            "Preview template instead" gives an accurate render. */}
                        {!hideBrokenRender && (
                        <Box
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            overflow="hidden"
                        >
                            {showSource ? (
                                <Box
                                    as="pre"
                                    m={0}
                                    p={3}
                                    maxH="580px"
                                    overflow="auto"
                                    bg="gray.900"
                                    color="gray.100"
                                    fontSize="11px"
                                    whiteSpace="pre-wrap"
                                    wordBreak="break-word"
                                >
                                    {html}
                                </Box>
                            ) : (
                                /* ET_046: the sandbox listed only allow-same-origin, which leaves
                                   form submission blocked — an AMP Form block rendered its inputs
                                   here but the Submit button could never do anything. allow-forms
                                   restores submission; allow-scripts is deliberately still absent,
                                   so script in template content cannot run.
                                   allow-popups is needed too: the exported form posts with
                                   target="_blank", and a sandboxed frame without it has the new
                                   window blocked, which looks exactly like a dead button. */
                                <iframe
                                    // Keyed on the content so React replaces the element instead
                                    // of mutating srcDoc on a live iframe — the same guard the
                                    // Email Templates preview uses. Re-assigning srcDoc in place
                                    // is not reliably re-parsed, which can leave a stale or
                                    // half-applied document behind when the modal is reused for
                                    // a different row.
                                    key={html.length}
                                    srcDoc={html || '<p style="padding:16px;color:#718096">No content yet</p>'}
                                    title="Email HTML Preview"
                                    style={{ width: '100%', height: '580px', border: 'none', display: 'block', background: 'white' }}
                                    sandbox="allow-same-origin allow-forms allow-popups"
                                />
                            )}
                        </Box>
                        )}
                    </Box>
                </ModalBody>
                <ModalFooter>
                    <Button
                        size="sm"
                        border={"1px solid #0000001a"}
                        color={"#0a0a0a"}
                        bg={"white"}
                        _hover={{ bg: "#e2e8f0 !important", color: "black !important" }}
                        onClick={onEmailPreviewClose}
                    >
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

export default EmailPreviewModal
